import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import { env } from '../config/env';

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit?: number;
  offset?: number;
}

export interface CrudAdapter<TItem extends { id?: number | string | null }, TCreate, TUpdate> {
  list(params: {
    offset: number;
    limit: number;
    sortName?: string;
    sortDirection?: 'asc' | 'desc';
    filter?: Record<string, unknown>;
  }): Promise<PaginatedResponse<TItem>>;
  byKey?(id: number | string): Promise<TItem>;
  create?(payload: TCreate): Promise<{ id: number | string }>;
  update?(payload: TUpdate): Promise<TItem | { id: number | string }>;
  remove?(id: number | string): Promise<void>;
}

export interface UseCrudDataSourceOptions {
  /** Field name used as the primary key. */
  keyExpr?: string;
  /** Static filters merged into every list call. */
  baseFilter?: Record<string, unknown>;
  /** Default page size when DevExtreme doesn't supply one. */
  defaultPageSize?: number;
}

/**
 * Creates a DevExtreme CustomStore tied to an API CRUD adapter.
 * Maps DevExtreme load options (skip/take/sort) to backend params (offset/limit/sort.*).
 */
export function useCrudDataSource<TItem extends { id?: number | string | null }, TCreate = TItem, TUpdate = TItem>(
  adapter: CrudAdapter<TItem, TCreate, TUpdate>,
  opts: UseCrudDataSourceOptions = {},
): CustomStore<TItem> {
  const pageSize = opts.defaultPageSize ?? env.defaultPageSize;

  return new CustomStore<TItem>({
    key: opts.keyExpr ?? 'id',
    loadMode: 'processed',
    async load(loadOptions: LoadOptions) {
      const skip = typeof loadOptions.skip === 'number' ? loadOptions.skip : 0;
      const take = typeof loadOptions.take === 'number' ? loadOptions.take : pageSize;
      let sortName: string | undefined;
      let sortDirection: 'asc' | 'desc' | undefined;
      const sort = loadOptions.sort;
      if (Array.isArray(sort) && sort.length > 0) {
        const first = sort[0] as { selector?: string; desc?: boolean };
        if (first?.selector) {
          sortName = first.selector;
          sortDirection = first.desc ? 'desc' : 'asc';
        }
      } else if (sort && typeof sort === 'object') {
        const s = sort as { selector?: string; desc?: boolean };
        if (s.selector) {
          sortName = s.selector;
          sortDirection = s.desc ? 'desc' : 'asc';
        }
      }
      const result = await adapter.list({
        offset: skip,
        limit: take,
        sortName,
        sortDirection,
        filter: opts.baseFilter,
      });
      return { data: result.items, totalCount: result.total };
    },
    async byKey(key) {
      if (!adapter.byKey) throw new Error('byKey not supported by adapter');
      return adapter.byKey(key as number | string);
    },
    async insert(values) {
      if (!adapter.create) throw new Error('insert not supported by adapter');
      const res = await adapter.create(values as unknown as TCreate);
      return { ...(values as object), id: res.id } as unknown as TItem;
    },
    async update(key, values) {
      if (!adapter.update) throw new Error('update not supported by adapter');
      const payload = { id: key, ...(values as object) } as unknown as TUpdate;
      const res = await adapter.update(payload);
      return res as unknown as TItem;
    },
    async remove(key) {
      if (!adapter.remove) throw new Error('remove not supported by adapter');
      await adapter.remove(key as number | string);
    },
  });
}
